import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "../components/Barcode";
import { declarations } from "../mock";

/* ---------- Small reusable field cell ---------- */
const Field = ({ num, en, ar, value, height = 42, valueClass = "", numRight = true, noBorderT = false, noBorderR = false, noBorderL = false, noBorderB = false }) => (
  <div
    className={`dec-cell ${noBorderT ? "border-t-0" : ""} ${noBorderR ? "border-r-0" : ""} ${noBorderL ? "border-l-0" : ""} ${noBorderB ? "border-b-0" : ""}`}
    style={{ minHeight: height, height }}
  >
    {num && (numRight ? <span className="field-num">{num}</span> : <span className="field-num-left">{num}</span>)}
    <div className="label-en">{en}</div>
    {ar && <div className="label-ar">{ar}</div>}
    {value !== undefined && (
      <div className={`value ${valueClass}`}>{value || ""}</div>
    )}
  </div>
);

/* ---------- Header table column cell ---------- */
const HCol = ({ num, ar, en, w }) => (
  <div className="text-center py-1 border-r border-black last:border-r-0" style={{ width: w || undefined }}>
    <div className="text-[9.5px] font-bold leading-none">{num}</div>
    {ar && <div className="text-[9px] font-arabic leading-tight mt-0.5">{ar}</div>}
    <div className="text-[8.5px] font-bold leading-tight uppercase mt-0.5">{en}</div>
  </div>
);

const DeclarationPage = () => {
  const { decNo } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      setData(declarations[decNo] || null);
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [decNo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p>Declaration not found.</p>
      </div>
    );
  }

  const PROD_DOMAIN = "https://misral2newdubaitrade.ac";
  const verifyUrl = `${PROD_DOMAIN}/declaration/${data.dec_no}`;

  return (
    <div className="min-h-screen bg-white text-left" style={{ overflowX: "auto" }}>
      <div className="dec-doc mx-auto" style={{ minWidth: 940, maxWidth: 940, padding: "24px 12px" }}>

        {/* ================ HEADER ================ */}
        <div className="grid grid-cols-12 items-center pb-2 border-b border-black">
          <div className="col-span-2 flex items-center justify-center">
            <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_qr-scanner-verify/artifacts/3eu4ny3e_IMG_3438.jpeg" alt="GCC" className="h-16 w-auto object-contain" />
          </div>
          <div className="col-span-8 px-3">
            <div className="text-[11px] text-black mb-1">{data.page}</div>
            <div className="flex items-center justify-center gap-4">
              <div className="text-black">
                <div className="text-[16px] font-bold leading-tight">United Arab Emirates</div>
                <div className="text-[16px] font-bold leading-tight text-center">UAE Customs</div>
                <div className="text-[16px] font-bold leading-tight text-center">Dubai Customs</div>
              </div>
              <div className="font-arabic text-black text-right">
                <div className="text-[17px] font-bold leading-tight">الإمارات العربية المتحدة</div>
                <div className="text-[17px] font-bold leading-tight">جمارك الإمارات</div>
                <div className="text-[17px] font-bold leading-tight">جمارك دبي</div>
              </div>
            </div>
          </div>
          <div className="col-span-2 flex items-center justify-center">
            <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_qr-scanner-verify/artifacts/8old01tu_IMG_3437.jpeg" alt="UAE Customs" className="h-16 w-auto object-contain" />
          </div>
        </div>

        {/* ================ ROW 2 : Customs Declaration + 4 numbered fields ================ */}
        <div className="grid grid-cols-12 mt-2 items-stretch">
          <div className="col-span-3 pl-1 pr-2 pt-1">
            <div className="text-[16px] font-bold text-black">Customs Declaration</div>
            <div className="text-[11px] text-black">{data.dec_subtype}</div>
            <div className="text-[13px] font-bold font-arabic text-black mt-0.5">بيان جمركي</div>
          </div>
          <div className="col-span-9 grid grid-cols-4 border border-black">
            <Field num="4" en="Port Type" ar="نوع المنفذ" value={data.port_type} height={44} noBorderT noBorderB noBorderL />
            <Field num="3" en="DEC Type" ar="نوع البيان" value={data.dec_type} height={44} noBorderT noBorderB />
            <Field num="2" en="DEC Date" ar="تاريخ البيان" value={data.dec_date} height={44} noBorderT noBorderB />
            <div className="dec-cell border-t-0 border-b-0 border-r-0" style={{ minHeight: 44, height: 44 }}>
              <span className="field-num">1</span>
              <div className="label-en">DEC No</div>
              <div className="label-ar">رقم البيان</div>
              <div className="value" style={{ marginTop: 12 }}>{data.dec_no}</div>
              <div className="text-[10px] text-black leading-none">{data.dec_barcode}</div>
            </div>
          </div>
        </div>

        {/* ================ MAIN BODY : fields 5-21 with vertical labels ================ */}
        <div className="grid mt-1" style={{ gridTemplateColumns: "20px 1fr 20px" }}>
          <div className="side-label">CARRIER&apos;S AGENT USE</div>

          <div className="grid grid-cols-12 border border-black">
            {/* LEFT column (span 4) */}
            <div className="col-span-4">
              <Field num="7" en="Net Weight" ar="الوزن الصافي" value={data.packages.net} height={42} noBorderT noBorderL />
              <Field num="10" en="Gross Weight" ar="الوزن القائم" value={`${data.gross_weight} (${data.gross_weight_unit})`} height={42} noBorderT noBorderL />
              <Field num="13" en="Measurement" ar="القياس" value={data.measurement} height={42} noBorderT noBorderL />
              <Field num="16" en="No. of Packages" ar="عدد الطرود" value={data.no_of_packages} height={42} noBorderT noBorderL />
              <div className="dec-cell border-t-0 border-l-0 border-b-0" style={{ minHeight: 140, height: 140 }}>
                <span className="field-num">19</span>
                <div className="label-en">Marks &amp; Numbers</div>
                <div className="label-ar">العلامات والأرقام</div>
                <div className="value text-[10px] leading-tight whitespace-pre-line" style={{ marginTop: 14 }}>{data.marks_numbers}</div>
              </div>
            </div>

            {/* MIDDLE column (span 4) */}
            <div className="col-span-4">
              <Field num="6" en="Importer / Exporter" ar="المستورد/المصدر" value={`${data.importer_code} - ${data.importer_name}`} height={42} noBorderT />
              <Field num="9" en="Intercessor Co." ar="الشركة الوسيطة" value={`${data.intercessor_code} - ${data.intercessor_name}`} height={42} noBorderT />
              <Field num="12" en="Commercial Reg. No." ar="رقم السجل التجاري" value={data.commercial_reg_no} height={42} noBorderT />
              <Field num="12A" en="TIN No." ar="الرقم الضريبي" value={data.tin_no} height={42} noBorderT />
              <Field num="15" en="Exported To" ar="المصدّر إليه" value={data.exported_to} height={35} noBorderT />
              <Field num="18" en="Port of Loading" ar="ميناء الشحن" value={data.port_of_loading} height={35} noBorderT />
              <Field num="20" en="Port of Discharge" ar="ميناء التفريغ" value={data.port_of_discharge} height={35} noBorderT />
              <Field num="21" en="Destination" ar="جهة المقصد" value={data.destination} height={35} noBorderT noBorderB />
            </div>

            {/* RIGHT column (span 4) */}
            <div className="col-span-4">
              <Field num="5" en="Delivery Order No." ar="رقم إذن التسليم" value={data.delivery_order_no} height={42} noBorderT noBorderR />
              <Field num="8" en="Carrier's \ Captain \ Driver" ar="الناقلة/القبطان/السائق" value={data.carrier_captain} height={42} noBorderT noBorderR />
              <Field num="11" en="Carrier's Name" ar="اسم الناقلة" value={data.carrier_name} height={42} noBorderT noBorderR />
              <Field num="14" en="Voyage / Flight No." ar="رقم الرحلة" value={data.voyage_no} height={42} noBorderT noBorderR />
              <div className="dec-cell border-t-0 border-r-0 border-b-0" style={{ minHeight: 140, height: 140 }}>
                <span className="field-num">17</span>
                <div className="label-en">B\L - AWB No. / MANIF.</div>
                <div className="label-ar">رقم البوليصة/المنافيست</div>
                <div className="value" style={{ marginTop: 14 }}>{data.bill_awb_no}</div>
                <div className="text-[10px] text-black mt-1 leading-tight">{data.bill_awb_loc}</div>
                <div className="text-[10px] text-black leading-tight">{data.bill_awb_pr}</div>
              </div>
            </div>
          </div>

          <div className="side-label-ar">لاستعمال وكيل الناقلة</div>
        </div>

        {/* ================ GOODS TABLE (22 - 31) ================ */}
        <div className="mt-1 border border-black">
          {/* Header row */}
          <div className="grid border-b border-black" style={{ gridTemplateColumns: "9% 9% 8% 12% 6% 6% 12% 6% 22% 7% 3%" }}>
            <HCol num="31" ar="إجمالي الرسوم" en="TOTAL DUTY AED" />
            <HCol num="30" ar="نوع الإيراد" en="INCOME TYPE" />
            <HCol num="29" ar="فئة الرسم" en="D. RATE" />
            <HCol num="28" ar="القيمة بالعملة المحلية" en="CIF LOCAL VALUE AED" />
            {/* Currency merged header spans 2 */}
            <div className="col-span-2 text-center border-r border-black" style={{ gridColumn: "span 2" }}>
              <div className="text-[9px] font-arabic font-bold mt-0.5">العملة</div>
              <div className="text-[8.5px] font-bold uppercase leading-tight">CURRENCY</div>
              <div className="grid grid-cols-2 border-t border-black mt-0.5">
                <div className="text-center border-r border-black py-0.5">
                  <div className="text-[9px] font-bold leading-none">27</div>
                  <div className="text-[8.5px] font-arabic">السعر</div>
                  <div className="text-[8px] font-bold">VALUE</div>
                </div>
                <div className="text-center py-0.5">
                  <div className="text-[9px] font-bold leading-none">26</div>
                  <div className="text-[8.5px] font-arabic">النوع</div>
                  <div className="text-[8px] font-bold">TYPE</div>
                </div>
              </div>
            </div>
            <HCol num="25" ar="القيمة بالعملة الأجنبية" en="FOREIGN VALUE" />
            <HCol num="24" ar="بلد المنشأ" en="ORIGIN" />
            <HCol num="23" ar="وصف البضاعة" en="GOODS DESCRIPTION" />
            <HCol num="22" ar="بند التعرفة" en="H. S. CODE" />
            <div className="text-center border-r-0" />
          </div>

          {/* Data rows */}
          {data.goods.map((g, i) => (
            <div key={i} className="grid text-center border-b border-black" style={{ gridTemplateColumns: "9% 9% 8% 12% 6% 6% 12% 6% 22% 7% 3%", minHeight: 26 }}>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.total_duty_aed}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.income_type}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.d_rate}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.cif_local_aed}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.currency_rate}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.currency_code}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.foreign_value}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.origin}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.description}</div>
              <div className="py-1 border-r border-black text-[11px] font-semibold">{g.hs_code}</div>
              <div className="py-1 text-[11px] font-semibold">{i + 1}</div>
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - data.goods.length) }).map((_, i) => (
            <div key={`e-${i}`} className="grid border-b border-black last:border-b-0" style={{ gridTemplateColumns: "9% 9% 8% 12% 6% 6% 12% 6% 22% 7% 3%", minHeight: 22 }}>
              <div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div className="border-r border-black" /><div />
            </div>
          ))}
        </div>

        {/* ================ EXEMPTION + PACKAGES + CUSTOMS RESTRICTIONS ================ */}
        <div className="grid mt-1" style={{ gridTemplateColumns: "20px 1fr 20px" }}>
          <div className="side-label">IMPORTER OR BROKER USE</div>

          <div className="grid grid-cols-12 border border-black">
            {/* Field 42 EXEMPTION */}
            <div className="col-span-4 dec-cell border-t-0 border-l-0 border-b-0" style={{ minHeight: 100, height: 100 }}>
              <span className="field-num-left">42</span>
              <div className="label-ar" style={{ position: "absolute", top: 2, right: 8 }}>رمز الإعفاءات</div>
              <div className="text-[10px] font-bold mt-2 pl-6">EXEMPTION OF DUTY CODE</div>
            </div>

            {/* Packages header + values */}
            <div className="col-span-6 border-r border-black">
              <div className="grid grid-cols-8 border-b border-black text-center text-[8.5px] font-bold">
                <div className="col-span-2 border-r border-black py-0.5">AIP<div className="font-arabic font-normal">م. م. ضارة</div></div>
                <div className="col-span-2 border-r border-black py-0.5">WEIGHT<div className="font-arabic font-normal">الوزن</div></div>
                <div className="border-r border-black py-0.5">ITEM<div className="font-arabic font-normal">الصنف</div></div>
                <div className="border-r border-black py-0.5">&nbsp;</div>
                <div className="border-r border-black py-0.5">PACKAGES<div className="font-arabic font-normal">الطرود</div></div>
                <div className="py-0.5">&nbsp;</div>
              </div>
              <div className="grid grid-cols-8 text-center" style={{ minHeight: 62 }}>
                {[
                  { num: "37B", en: "DUTY", ar: "الرسم", val: data.packages.duty },
                  { num: "37A", en: "NO.", ar: "النشرة", val: data.packages.no },
                  { num: "37", en: "GROSS", ar: "القائم", val: "" },
                  { num: "36", en: "NET", ar: "الصافي", val: data.packages.gross },
                  { num: "35", en: "UNIT", ar: "وحدة", val: data.packages.unit },
                  { num: "34", en: "QTY", ar: "الكمية", val: data.packages.item },
                  { num: "33", en: "TYPE", ar: "النوع", val: data.packages.type },
                  { num: "32", en: "QTY", ar: "الكمية", val: data.packages.qty },
                ].map((p, i) => (
                  <div key={i} className={`py-0.5 ${i < 7 ? "border-r border-black" : ""}`}>
                    <div className="text-[9px] font-bold">{p.num}</div>
                    <div className="text-[8px] font-arabic">{p.ar}</div>
                    <div className="text-[8.5px] font-bold">{p.en}</div>
                    <div className="text-[10px] font-bold mt-2">{p.val || "\u00A0"}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customs Restrictions */}
            <div className="col-span-2">
              <div className="text-center border-b border-black py-0.5">
                <div className="text-[9.5px] font-bold">CUSTOMS RESTRICTIONS</div>
                <div className="text-[9px] font-arabic">القيود الجمركية</div>
              </div>
              <div className="grid grid-cols-2" style={{ minHeight: 78 }}>
                <div className="dec-cell border-t-0 border-l-0 border-b-0">
                  <span className="field-num">41</span>
                  <div className="label-ar" style={{ right: 22 }}>مرجع الفسح</div>
                  <div className="text-[9px] font-bold mt-3">RELEASE REF.</div>
                </div>
                <div className="dec-cell border-t-0 border-r-0 border-b-0">
                  <span className="field-num">40</span>
                  <div className="label-ar" style={{ right: 22 }}>الجهة</div>
                  <div className="text-[9px] font-bold mt-3">AGENCY</div>
                  <div className="text-[10px] font-bold text-right absolute bottom-1 right-2">1</div>
                </div>
              </div>
            </div>
          </div>

          <div className="side-label-ar">لاستعمال المستورد أو المخلص</div>
        </div>

        {/* ================ BOTTOM 3 COLUMNS ================ */}
        <div className="grid grid-cols-12 mt-1 gap-1">
          {/* ---- LEFT COL : AED breakdown + payment method (DASHED BORDER) ---- */}
          <div className="col-span-4" style={{ border: "1.5px dashed #000", padding: "2px" }}>
            <div className="border border-black">
              {/* AED / DUTY / رسوم header row */}
              <div className="grid grid-cols-3 border-b border-black">
                <div className="dec-cell border-t-0 border-l-0 border-b-0 py-0.5" style={{ minHeight: 24 }}>
                  <div className="text-[10px] font-bold">AED</div>
                  <div className="text-[9px] font-arabic">درهم</div>
                </div>
                <div className="dec-cell border-t-0 border-b-0 py-0.5" style={{ minHeight: 24 }}>
                  <div className="text-[10px] font-bold">DUTY</div>
                </div>
                <div className="dec-cell border-t-0 border-r-0 border-b-0 py-0.5 text-right" style={{ minHeight: 24 }}>
                  <div className="text-[9px] font-arabic">الرسوم</div>
                </div>
              </div>
              {[
                { code: "TOTAL DUTY", ar: "الرسوم الجمركية", val: data.aed_totals.total_duty, num: "48" },
                { code: "VAT", ar: "ضريبة القيمة المضافة", val: data.aed_totals.vat, num: "48A" },
                { code: "EXCISE TAX", ar: "ضريبة انتقائية", val: data.aed_totals.excise_tax, num: "48B" },
                { code: "ANTI DUMPING", ar: "رسم ممارسات ضارة", val: data.aed_totals.anti_dumping, num: "48C" },
                { code: "HANDLING", ar: "رسوم المناولة", val: data.aed_totals.handling, num: "49" },
                { code: "OTHER CHARGES", ar: "رسوم أخرى", val: data.aed_totals.other_charges, num: "50" },
              ].map((r) => (
                <div key={r.num} className="grid grid-cols-3 border-b border-black">
                  <div className="dec-cell border-t-0 border-l-0 border-b-0 py-0.5" style={{ minHeight: 22 }}>
                    <div className="text-[10px] font-semibold">{r.val || ""}</div>
                  </div>
                  <div className="dec-cell border-t-0 border-b-0 py-0.5" style={{ minHeight: 22 }}>
                    <div className="text-[9.5px] font-semibold">{r.code}</div>
                  </div>
                  <div className="dec-cell border-t-0 border-r-0 border-b-0 py-0.5 text-right" style={{ minHeight: 22 }}>
                    <div className="text-[9.5px] font-bold absolute top-1 left-2">{r.num}</div>
                    <div className="text-[9px] font-arabic">{r.ar}</div>
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-3 border-b border-black">
                <div className="dec-cell border-t-0 border-l-0 border-b-0 py-0.5" style={{ minHeight: 24 }}>
                  <div className="text-[10px] font-bold">DEFINITE <span className="font-semibold">{data.aed_totals.definite}</span></div>
                </div>
                <div className="dec-cell border-t-0 border-b-0 py-0.5 text-right" style={{ minHeight: 24 }}>
                  <div className="text-[9px] font-arabic">قطعي</div>
                  <div className="text-[9px] font-arabic">إجمالي الرسوم</div>
                </div>
                <div className="dec-cell border-t-0 border-r-0 border-b-0 py-0.5 text-right" style={{ minHeight: 24 }}>
                  <div className="text-[9.5px] font-bold absolute top-1 left-2">51</div>
                  <div className="text-[9px] font-bold">TOTAL FEE</div>
                </div>
              </div>
              <div className="grid grid-cols-3 border-b border-black">
                <div className="dec-cell border-t-0 border-l-0 border-b-0 py-0.5" style={{ minHeight: 22 }}>
                  <div className="text-[10px] font-bold">INSURED</div>
                </div>
                <div className="dec-cell border-t-0 border-b-0 py-0.5 text-right" style={{ minHeight: 22 }}>
                  <div className="text-[9px] font-arabic">تأمين</div>
                </div>
                <div className="dec-cell border-t-0 border-r-0 border-b-0 py-0.5 text-right" style={{ minHeight: 22 }}>
                  <div className="text-[9.5px] font-bold absolute top-1 left-2">52</div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b border-black" style={{ minHeight: 72 }}>
                <span className="field-num">53</span>
                <div className="label-en">Payment Method</div>
                <div className="label-ar">طريقة الدفع</div>
                <div className="flex mt-3 gap-2 items-start">
                  <div className="text-[9px] font-bold w-6">No.</div>
                  <div className="text-[9px] leading-tight whitespace-pre-line flex-1">{data.aed_totals.payment_method}</div>
                  <div className="text-[9px] font-bold ml-1">54</div>
                </div>
              </div>

              {/* 55 - 59 rows */}
              {[
                { num: "55", en: "DATE", ar: "تاريخ", val: data.receipt_details.date },
                { num: "56", en: "BANK", ar: "بنك", val: data.receipt_details.bank },
                { num: "57", en: "RECEIPT NO.", ar: "رقم أيصال الدفع", val: data.receipt_details.receipt_no },
                { num: "58", en: "DATE", ar: "تاريخ", val: "" },
                { num: "59", en: "BANK", ar: "بنك", val: "" },
              ].map((r, i, arr) => (
                <div key={r.num} className={`dec-cell border-t-0 border-l-0 border-r-0 ${i === arr.length - 1 ? "border-b-0" : "border-b border-black"} flex items-center py-0.5`} style={{ minHeight: 22 }}>
                  <div className="text-[9.5px] font-bold w-24">{r.en}</div>
                  <div className="text-[10px] flex-1">{r.val}</div>
                  <div className="text-[9px] font-arabic mr-2">{r.ar}</div>
                  <div className="text-[9.5px] font-bold w-4 text-right">{r.num}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ---- MIDDLE COL : CLEARING AGENT + LICENCE + EXIT PORT + QR (DASHED BORDER) ---- */}
          <div className="col-span-4" style={{ border: "1.5px dashed #000", padding: "2px" }}>
            <div className="border border-black">
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b border-black" style={{ minHeight: 66 }}>
                <span className="field-num">38</span>
                <div className="label-en">Clearing Agent</div>
                <div className="label-ar">المخلص الجمركي</div>
                <div className="value text-[11px]" style={{ marginTop: 14 }}>{data.clearing_agent_code}</div>
                <div className="text-[11px] font-semibold">{data.clearing_agent_name}</div>
              </div>
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b border-black" style={{ minHeight: 60 }}>
                <span className="field-num">39</span>
                <div className="label-en">Licence No.</div>
                <div className="label-ar">رقم الرخصة</div>
                <div className="value" style={{ marginTop: 18 }}>{data.clearing_agent_licence}</div>
              </div>
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b border-black" style={{ minHeight: 46 }}>
                <span className="field-num">46</span>
                <div className="label-en">Exit Port</div>
                <div className="label-ar">جمرك الخروج</div>
                <div className="value">{data.exit_port}</div>
              </div>
              {/* QR CODE cell */}
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b-0 flex items-center justify-around" style={{ minHeight: 130 }}>
                <div className="p-1 bg-white">
                  <QRCodeSVG value={verifyUrl} size={104} level="M" includeMargin={false} />
                </div>
                <div className="text-center">
                  <div className="text-[11px] font-bold">47</div>
                  <div className="text-[9.5px] font-arabic mt-8 leading-tight">رمز الاستجابة السريعة</div>
                  <div className="text-[10px] font-bold">QR Code</div>
                </div>
              </div>
            </div>
          </div>

          {/* ---- RIGHT COL : UNIFIED / GCC AEO / OTHER REMARKS (DASHED BORDER) ---- */}
          <div className="col-span-4" style={{ border: "1.5px dashed #000", padding: "2px" }}>
            <div className="border border-black">
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b border-black" style={{ minHeight: 46 }}>
                <span className="field-num">43</span>
                <div className="label-en">Unified Customs Code</div>
                <div className="label-ar">الرقم المرجعي الموحد للمستورد/المصدر</div>
                <div className="value">{data.unified_customs_code}</div>
              </div>
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b border-black" style={{ minHeight: 46 }}>
                <span className="field-num">44</span>
                <div className="label-en">GCC AEO Code</div>
                <div className="label-ar">رمز المشغل الاقتصادي المعتمد الخليجي</div>
                <div className="value">{data.gcc_aeo_code}</div>
              </div>
              <div className="dec-cell border-t-0 border-l-0 border-r-0 border-b-0" style={{ minHeight: 210 }}>
                <span className="field-num">45</span>
                <div className="label-en">Other Remarks</div>
                <div className="label-ar">ملاحظات أخرى</div>
                <div className="value text-[10px] leading-relaxed whitespace-pre-line" style={{ marginTop: 16 }}>{data.other_remarks}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ================ FOOTER : Barcode ================ */}
        <div className="mt-3 flex items-end justify-between">
          <div className="text-[10px] text-black">{data.footer_ref}</div>
          <div className="flex-1 flex justify-center">
            <Barcode value={data.dec_barcode} height={44} width={1.6} />
          </div>
          <div className="text-[10px] text-black">{data.footer_date}</div>
        </div>
      </div>
    </div>
  );
};

export default DeclarationPage;
